<template>
  <Teleport to="body">
    <div v-if="open" class="int-modal-overlay int-modal-overlay--stacked" @click.self="cancel">
      <div class="int-modal int-modal--xl int-schedule-editor" role="dialog" aria-modal="true" @click.stop>
        <div class="int-modal__accent" />
        <header class="int-modal__header">
          <div class="int-modal__icon-wrap">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div class="int-modal__titles">
            <h2>{{ t('integracao.agendamentoForms.scheduleTitle') }}</h2>
            <p class="int-modal__subtitle">{{ t('integracao.agendamentoForms.scheduleEditorHint') }}</p>
          </div>
          <button type="button" class="int-modal__close" :aria-label="t('common.close')" @click="cancel">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </header>

        <div class="int-modal__body int-schedule-editor__body">
          <div class="int-schedule-editor__toolbar">
            <div class="int-schedule-editor__modes">
              <button
                type="button"
                class="int-schedule-mode"
                :class="{ 'int-schedule-mode--active': paintMode === 'add' }"
                @click="paintMode = 'add'"
              >
                {{ t('integracao.agendamentoForms.scheduleModeAdd') }}
              </button>
              <button
                type="button"
                class="int-schedule-mode"
                :class="{ 'int-schedule-mode--active': paintMode === 'remove' }"
                @click="paintMode = 'remove'"
              >
                {{ t('integracao.agendamentoForms.scheduleModeRemove') }}
              </button>
            </div>
            <div class="int-schedule-editor__actions">
              <button type="button" class="int-schedule-day__action" @click="copyWeekdays">
                {{ t('integracao.agendamentoForms.copyWeekdays') }}
              </button>
              <button type="button" class="int-schedule-day__action" @click="clearAll">
                {{ t('integracao.agendamentoForms.clearAll') }}
              </button>
            </div>
          </div>

          <div class="int-schedule-editor__status" role="status" aria-live="polite">
            <p
              class="int-schedule-editor__status-text"
              :class="{
                'int-schedule-editor__status-text--active': !!clickAnchor,
                'int-schedule-editor__status-text--error': !hasAnyScheduleSlot(localConfig) && !clickAnchor
              }"
            >
              <template v-if="clickAnchor">
                {{ t('integracao.agendamentoForms.scheduleAnchorHint', {
                  day: t(`integracao.agendamentoForms.weekdays.${clickAnchor.dayKey}`),
                  time: ALL_SLOT_TIMES[clickAnchor.idx]
                }) }}
              </template>
              <template v-else-if="!hasAnyScheduleSlot(localConfig)">
                {{ t('integracao.agendamentoForms.scheduleRequired') }}
              </template>
              <template v-else>
                {{ t('integracao.agendamentoForms.scheduleStatusIdle') }}
              </template>
            </p>
          </div>

          <div class="int-schedule-matrix-wrap">
            <div class="int-schedule-matrix">
              <div
                v-for="dayKey in WEEKDAY_KEYS"
                :key="dayKey"
                class="int-schedule-matrix__row"
                :class="{ 'int-schedule-matrix__row--weekend': dayKey === '0' || dayKey === '6' }"
              >
                <div class="int-schedule-matrix__day-col">
                  <span class="int-schedule-matrix__day-name">{{ t(`integracao.agendamentoForms.weekdays.${dayKey}`) }}</span>
                  <button type="button" class="int-schedule-matrix__clear" @click="clearDay(dayKey)">
                    {{ t('integracao.agendamentoForms.clearDay') }}
                  </button>
                </div>
                <div class="int-schedule-matrix__track">
                  <button
                    v-for="slot in ALL_SLOT_TIMES"
                    :key="`${dayKey}-${slot}`"
                    type="button"
                    class="int-schedule-cell"
                    :class="cellClasses(dayKey, slot)"
                    :title="`${t(`integracao.agendamentoForms.weekdays.${dayKey}`)} · ${slot}`"
                    @mousedown.prevent="onCellMouseDown(dayKey, slot)"
                    @mouseenter="onCellMouseEnter(dayKey, slot)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer class="int-modal__footer">
          <button type="button" class="int-btn int-btn--ghost" @click="cancel">{{ t('common.cancel') }}</button>
          <button
            type="button"
            class="int-btn int-btn--primary"
            :disabled="!hasAnyScheduleSlot(localConfig)"
            @click="apply"
          >
            {{ t('integracao.agendamentoForms.scheduleApply') }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  ALL_SLOT_TIMES,
  WEEKDAY_KEYS,
  normalizeScheduleConfig,
  hasAnyScheduleSlot,
  isSlotEnabled,
  applySlotRange,
  setAllSlotsForDay,
  copyWeekdaysFrom,
  slotIndex
} from '@/utils/agendamentoScheduleConfig'
import '@/assets/styles/integration-modal.css'

const props = defineProps({
  open: Boolean,
  scheduleConfig: { type: Object, default: null }
})

const emit = defineEmits(['close', 'apply'])

const { t } = useI18n()

const localConfig = ref(normalizeScheduleConfig(null))
const paintMode = ref('add')
const clickAnchor = ref(null)

const painting = ref(false)
const paintStart = ref(null)
const paintEnd = ref(null)
const paintMoved = ref(false)

watch(() => props.open, (v) => {
  if (!v) return
  localConfig.value = normalizeScheduleConfig(props.scheduleConfig)
  paintMode.value = 'add'
  clickAnchor.value = null
  resetPaint()
})

function resetPaint () {
  painting.value = false
  paintStart.value = null
  paintEnd.value = null
  paintMoved.value = false
}

function onDocumentMouseUp () {
  if (!painting.value || !paintStart.value) {
    resetPaint()
    document.removeEventListener('mouseup', onDocumentMouseUp)
    return
  }

  const { dayKey, idx: startIdx } = paintStart.value
  const endIdx = paintEnd.value?.idx ?? startIdx

  if (paintMoved.value) {
    applyRange(dayKey, startIdx, endIdx)
    clickAnchor.value = null
  } else if (clickAnchor.value?.dayKey === dayKey) {
    applyRange(dayKey, clickAnchor.value.idx, startIdx)
    clickAnchor.value = null
  } else {
    clickAnchor.value = { dayKey, idx: startIdx }
  }

  resetPaint()
  document.removeEventListener('mouseup', onDocumentMouseUp)
}

function onCellMouseDown (dayKey, slot) {
  const idx = slotIndex(slot)
  if (idx < 0) return
  painting.value = true
  paintStart.value = { dayKey, idx }
  paintEnd.value = { dayKey, idx }
  paintMoved.value = false
  document.addEventListener('mouseup', onDocumentMouseUp)
}

function onCellMouseEnter (dayKey, slot) {
  if (!painting.value || paintStart.value?.dayKey !== dayKey) return
  const idx = slotIndex(slot)
  paintEnd.value = { dayKey, idx }
  if (idx !== paintStart.value.idx) paintMoved.value = true
}

function applyRange (dayKey, startIdx, endIdx) {
  const from = ALL_SLOT_TIMES[Math.min(startIdx, endIdx)]
  const to = ALL_SLOT_TIMES[Math.max(startIdx, endIdx)]
  localConfig.value = applySlotRange(localConfig.value, dayKey, from, to, paintMode.value)
}

function isPreview (dayKey, slot) {
  if (painting.value && paintStart.value?.dayKey === dayKey && paintEnd.value?.dayKey === dayKey) {
    const idx = slotIndex(slot)
    const a = Math.min(paintStart.value.idx, paintEnd.value.idx)
    const b = Math.max(paintStart.value.idx, paintEnd.value.idx)
    return idx >= a && idx <= b
  }
  if (clickAnchor.value?.dayKey === dayKey) {
    return slotIndex(slot) === clickAnchor.value.idx
  }
  return false
}

function cellClasses (dayKey, slot) {
  const on = isSlotEnabled(localConfig.value, dayKey, slot)
  const preview = isPreview(dayKey, slot)
  return {
    'int-schedule-cell--on': on && !preview,
    'int-schedule-cell--preview': preview && paintMode.value === 'add',
    'int-schedule-cell--preview-remove': preview && paintMode.value === 'remove',
    'int-schedule-cell--anchor': clickAnchor.value?.dayKey === dayKey && slotIndex(slot) === clickAnchor.value.idx && !painting.value
  }
}

function clearDay (dayKey) {
  localConfig.value = setAllSlotsForDay(localConfig.value, dayKey, false)
  if (clickAnchor.value?.dayKey === dayKey) clickAnchor.value = null
}

function clearAll () {
  for (const key of WEEKDAY_KEYS) {
    localConfig.value = setAllSlotsForDay(localConfig.value, key, false)
  }
  clickAnchor.value = null
}

function copyWeekdays () {
  localConfig.value = copyWeekdaysFrom(localConfig.value, '1')
}

function cancel () {
  emit('close')
}

function apply () {
  if (!hasAnyScheduleSlot(localConfig.value)) return
  emit('apply', normalizeScheduleConfig(localConfig.value))
  emit('close')
}

onBeforeUnmount(() => {
  document.removeEventListener('mouseup', onDocumentMouseUp)
})
</script>
